import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Roles } from '../../../models/roles';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/users.service';

const DEFAULT_SCHEDULE_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function noWhitespace(control: AbstractControl): ValidationErrors | null {
  const value = control.value || '';
  return /\s/.test(value) ? { whitespace: true } : null;
}

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss',
})
export class AddUserComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() userCreated = new EventEmitter<any>();

  loadingProviders = false;
  loadingBaseConfiguration = false;
  creating = false;
  checkingUniqueness = false;
  private uniquenessRequests = 0;
  identityInUse: Record<
    'email' | 'document_number' | 'company_registration_number',
    boolean
  > = {
    email: false,
    document_number: false,
    company_registration_number: false,
  };
  activeStep: 1 | 2 = 1;
  creatingStage: 'configuration' | 'user' | null = null;
  baseConfiguration: any = null;
  customConfigurationInitialized = false;

  roleOptions = [
    { label: 'Cliente', value: Roles.Client },
    { label: 'Proveedor', value: Roles.Provider },
    { label: 'Trader', value: Roles.Trader },
    { label: 'Administrador / Soporte', value: Roles.Admin },
  ];

  userTypeOptions = [
    { label: 'Usuario', value: 'user' },
    { label: 'Subusuario', value: 'subuser' },
  ];

  entityTypeOptions = [
    { label: 'Persona natural', value: 'natural' },
    { label: 'Persona jurídica', value: 'business' },
  ];

  documentTypeOptions = [
    { label: 'DNI', value: 'DNI' },
    { label: 'Pasaporte', value: 'PASSPORT' },
    { label: 'Licencia con foto', value: 'PHOTO_LICENSE' },
  ];

  complianceStatuses = [
    { label: 'Pendiente', value: 'pending' },
    { label: 'Aprobado', value: 'approved' },
    { label: 'Rechazado', value: 'rejected' },
  ];

  tokenExpDayOptions = [
    { label: '30 días', value: 30 },
    { label: '90 días (default)', value: 90 },
    { label: '180 días', value: 180 },
    { label: '365 días', value: 365 },
  ];

  configurationOptions = [
    { label: 'Base del sistema', value: 'base' },
    { label: 'Personalizada para este usuario', value: 'custom' },
  ];

  private commonRequiredProfileFieldNames = ['email'] as const;

  private addressRequiredProfileFieldNames = [
    'address_line_1',
    'city',
    'postal_code',
    'state',
    'country',
  ] as const;

  private naturalRequiredProfileFieldNames = [
    'first_name',
    'surname',
  ] as const;

  private naturalProfileFieldNames = [
    ...this.naturalRequiredProfileFieldNames,
    'middle_name',
    'second_surname',
    'document_type',
    'document_number',
  ] as const;

  private businessRequiredProfileFieldNames = [
    'company_name',
    'company_registration_number',
  ] as const;

  private businessProfileFieldNames = [
    ...this.businessRequiredProfileFieldNames,
    'brand_name',
  ] as const;

  scheduleDays = DEFAULT_SCHEDULE_DAYS.map((name) => ({
    name,
    from: '00:00',
    to: '23:59',
  }));

  dayLabels: Record<string, string> = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
  };

  createUserForm = this.fb.group({
    user_type: ['user'],
    entity_type: ['natural'],
    username: ['', [Validators.required, noWhitespace]],
    password: [
      '',
      [Validators.required, Validators.minLength(6), noWhitespace],
    ],
    confirm_password: ['', [Validators.required, noWhitespace]],
    role_id: [null as number | null, [Validators.required]],
    status_compliance: ['pending'],
    client_partner_id: [null as number | null],
    white_label: [false],
    reliance_enabled: [false],
    end_user_token_exp_days: [90],
    email: [''],
    first_name: [''],
    middle_name: [''],
    surname: [''],
    second_surname: [''],
    company_name: [''],
    brand_name: [''],
    company_registration_number: [''],
    document_type: [''],
    document_number: [''],
    address_line_1: [''],
    address_line_2: [''],
    city: [''],
    postal_code: [''],
    state: [''],
    country: [''],
    configuration_type: ['base'],
    joint_balance: [false],
    single_currency: [false],
    fixed_fee: [0],
    percentage_fee: [0],
    max_p2p_amount: [null as number | null],
    max_transfer_amount: [null as number | null],
    cashout_providers_priorities: [[] as number[]],
  });

  availableProviders: any[] = [];
  addProviderModal = false;
  selectedProvider: any = null;
  priorities: number[] = [];

  constructor(
    private fb: FormBuilder,
    public userService: UserService,
    private auth: AuthService,
    private messageService: MessageService,
  ) {
    this.createUserForm.controls.configuration_type.valueChanges.subscribe(
      (value) => {
        if (value === 'custom') {
          this.applyBaseConfigurationDefaults();
          return;
        }

        this.customConfigurationInitialized = false;
      },
    );
    this.createUserForm.controls.user_type.valueChanges.subscribe(() => {
      this.applyCreationTypeRules();
      this.applyRequiredProfileRules();
    });
    this.createUserForm.controls.entity_type.valueChanges.subscribe(() => {
      this.clearIdentityInUse('document_number');
      this.clearIdentityInUse('company_registration_number');
      this.applyRequiredProfileRules();
    });
    this.createUserForm.controls.role_id.valueChanges.subscribe(() => {
      this.applyRequiredProfileRules();
    });
    (['email', 'document_number', 'company_registration_number'] as const).forEach(
      (fieldName) => {
        this.createUserForm.controls[fieldName].valueChanges.subscribe(() => {
          if (this.identityInUse[fieldName]) {
            this.setInUse(fieldName, false);
          }
        });
      },
    );
    this.applyCreationTypeRules();
    this.applyRequiredProfileRules();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue) {
      this.loadProviders();
      this.loadBaseConfiguration();
      this.applyCreationTypeRules();
      this.applyDefaultClientPartnerId();
      this.applyRequiredProfileRules();
    }
  }

  setVisible(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);

    if (value) {
      this.loadProviders();
      this.loadBaseConfiguration();
      this.applyCreationTypeRules();
      this.applyDefaultClientPartnerId();
      this.applyRequiredProfileRules();
      return;
    }

    if (!this.creating) {
      this.resetForm();
      this.addProviderModal = false;
    }
  }

  get partnerParentOptions(): { label: string; value: number }[] {
    const options = (this.userService.users || []).map((user: any) => ({
      label: user.username,
      value: Number(user.user_id),
    }));
    const loggedId = Number(this.auth.user?.user_id);
    if (
      Number.isFinite(loggedId) &&
      loggedId > 0 &&
      !options.some((o) => o.value === loggedId)
    ) {
      const username = this.auth.user?.username || 'Usuario actual';
      options.unshift({
        label: `${username} (ID ${loggedId}) — tú`,
        value: loggedId,
      });
    }
    return options
      .map((option) => ({
        ...option,
        label:
          this.userService.getUsernameById(option.value) ||
          (option.label || '').split(' (ID ')[0],
      }));
  }

  private defaultClientPartnerId(): number | null {
    if (!this.isSubuserCreation()) {
      return 1;
    }

    if (this.isAdmin()) {
      return null;
    }

    const loggedId = Number(this.auth.user?.user_id);
    return Number.isFinite(loggedId) && loggedId > 0 ? loggedId : null;
  }

  private applyDefaultClientPartnerId(force = false): void {
    const current = this.createUserForm.controls.client_partner_id.value;
    if (!force && current != null) return;
    this.createUserForm.patchValue({
      client_partner_id: this.defaultClientPartnerId(),
    });
  }

  isAdmin(): boolean {
    return this.auth.hasRole(Roles.Admin);
  }

  canCreateUsers(): boolean {
    return this.userService.canCreatePlatformUser();
  }

  canCreateSubusers(): boolean {
    return this.userService.canCreatePlatformSubuser();
  }

  get availableUserTypeOptions() {
    return this.userTypeOptions.filter((option) =>
      option.value === 'user'
        ? this.canCreateUsers()
        : this.canCreateSubusers(),
    );
  }

  canChooseUserType(): boolean {
    return this.availableUserTypeOptions.length > 1;
  }

  selectedUserType(): 'user' | 'subuser' {
    const selected = this.createUserForm.controls.user_type.value === 'subuser'
      ? 'subuser'
      : 'user';

    if (selected === 'user' && this.canCreateUsers()) {
      return 'user';
    }

    if (selected === 'subuser' && this.canCreateSubusers()) {
      return 'subuser';
    }

    return this.canCreateUsers() ? 'user' : 'subuser';
  }

  isSubuserCreation(): boolean {
    return this.selectedUserType() === 'subuser';
  }

  isBusinessEntity(): boolean {
    return this.createUserForm.controls.entity_type.value === 'business';
  }

  requiresPassword(): boolean {
    return !this.isSubuserCreation();
  }

  shouldShowProfileSection(): boolean {
    return this.isAdmin() || this.isSubuserCreation();
  }

  canChooseRole(): boolean {
    return this.isAdmin() && !this.isSubuserCreation();
  }

  isBoStaffRole(): boolean {
    return Number(this.createUserForm.controls.role_id.value) === Roles.Admin;
  }

  requiresAddress(): boolean {
    return !this.isBoStaffRole();
  }

  private applyRequiredProfileRules(): void {
    const allConditionalFields = [
      ...this.commonRequiredProfileFieldNames,
      ...this.addressRequiredProfileFieldNames,
      ...this.naturalProfileFieldNames,
      ...this.businessProfileFieldNames,
    ];
    allConditionalFields.forEach((fieldName) => {
      const control = this.createUserForm.controls[fieldName];
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.commonRequiredProfileFieldNames.forEach((fieldName) => {
      const control = this.createUserForm.controls[fieldName];
      const validators = fieldName === 'email' ? [Validators.email] : [];
      validators.unshift(Validators.required);
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    });

    if (this.requiresAddress()) {
      this.addressRequiredProfileFieldNames.forEach((fieldName) => {
        const control = this.createUserForm.controls[fieldName];
        control.setValidators([Validators.required]);
        control.updateValueAndValidity({ emitEvent: false });
      });
    }

    const identityRequiredFields = this.isBusinessEntity()
      ? this.businessRequiredProfileFieldNames
      : this.naturalRequiredProfileFieldNames;
    identityRequiredFields.forEach((fieldName) => {
      const control = this.createUserForm.controls[fieldName];
      control.setValidators([Validators.required]);
      control.updateValueAndValidity({ emitEvent: false });
    });
    this.restoreIdentityInUseErrors();
  }

  private applyCreationTypeRules(): void {
    const currentType = this.createUserForm.controls.user_type.value;
    if (currentType === 'user' && !this.canCreateUsers()) {
      this.createUserForm.controls.user_type.setValue('subuser', {
        emitEvent: false,
      });
    }

    if (
      this.createUserForm.controls.user_type.value === 'subuser' &&
      !this.canCreateSubusers() &&
      this.canCreateUsers()
    ) {
      this.createUserForm.controls.user_type.setValue('user', {
        emitEvent: false,
      });
    }

    const isSubuser = this.isSubuserCreation();
    const passwordControl = this.createUserForm.controls.password;
    const confirmControl = this.createUserForm.controls.confirm_password;
    const roleControl = this.createUserForm.controls.role_id;
    const parentControl = this.createUserForm.controls.client_partner_id;

    if (this.canChooseRole()) {
      roleControl.setValidators([Validators.required]);
      if (roleControl.value == null) {
        roleControl.setValue(Roles.Client, { emitEvent: false });
      }
    } else {
      roleControl.clearValidators();
      roleControl.setValue(Roles.Client, { emitEvent: false });
    }

    if (isSubuser) {
      passwordControl.clearValidators();
      confirmControl.clearValidators();
      passwordControl.setValue('', { emitEvent: false });
      confirmControl.setValue('', { emitEvent: false });
      this.createUserForm.patchValue(
        {
          status_compliance: 'pending',
          configuration_type: this.isAdmin()
            ? this.createUserForm.controls.configuration_type.value
            : 'base',
        },
        { emitEvent: false },
      );
    } else {
      passwordControl.setValidators([
        Validators.required,
        Validators.minLength(6),
        noWhitespace,
      ]);
      confirmControl.setValidators([Validators.required, noWhitespace]);
    }

    if (isSubuser && this.isAdmin()) {
      parentControl.setValidators([Validators.required]);
    } else {
      parentControl.clearValidators();
      this.applyDefaultClientPartnerId(true);
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });
    confirmControl.updateValueAndValidity({ emitEvent: false });
    roleControl.updateValueAndValidity({ emitEvent: false });
    parentControl.updateValueAndValidity({ emitEvent: false });
    this.applyRequiredProfileRules();
  }

  async loadProviders() {
    if (this.loadingProviders) return;

    this.loadingProviders = true;
    try {
      await this.userService.getUsers();
      this.refreshAvailableProviders();
      this.applyRequiredProfileRules();
    } finally {
      this.loadingProviders = false;
    }
  }

  loadBaseConfiguration() {
    if (!this.isAdmin()) return;
    if (this.loadingBaseConfiguration || this.baseConfiguration) return;

    this.loadingBaseConfiguration = true;
    this.userService.getBaseConfiguration().subscribe({
      next: (resp: any) => {
        this.baseConfiguration = resp?.data || null;
        this.loadingBaseConfiguration = false;

        if (this.isCustomConfiguration()) {
          this.applyBaseConfigurationDefaults();
        }
      },
      error: () => {
        this.loadingBaseConfiguration = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No pudimos cargar la configuración base.',
        });
      },
    });
  }

  applyBaseConfigurationDefaults() {
    if (this.customConfigurationInitialized || !this.baseConfiguration) return;

    const base = this.baseConfiguration;
    this.createUserForm.patchValue({
      joint_balance: !!base.joint_balance,
      single_currency: !!base.single_currency,
      fixed_fee: Number(base.fixed_fee ?? 0),
      percentage_fee: Number(base.percentage_fee ?? 0),
      max_p2p_amount: this.toNullableNumber(base.max_p2p_amount),
      max_transfer_amount: this.toNullableNumber(base.max_transfer_amount),
      cashout_providers_priorities: this.normalizePriorityList(
        base.cashout_providers_priorities,
      ),
    });
    this.priorities = this.normalizePriorityList(
      base.cashout_providers_priorities,
    );
    this.scheduleDays = this.buildScheduleDaysFromBase(base.schedule);
    this.customConfigurationInitialized = true;
    this.refreshAvailableProviders();
  }

  toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  normalizePriorityList(value: any): number[] {
    const priorities = Array.isArray(value) ? value : [];
    return priorities
      .map((priority) => Number(priority))
      .filter((priority) => Number.isFinite(priority));
  }

  buildScheduleDaysFromBase(schedule: any) {
    const baseSchedule =
      schedule && typeof schedule === 'object' ? schedule : {};
    return DEFAULT_SCHEDULE_DAYS.map((name) => ({
      name,
      from: baseSchedule[name]?.from || '00:00',
      to: baseSchedule[name]?.to || '23:59',
    }));
  }

  refreshAvailableProviders() {
    this.availableProviders = this.userService.providers.filter(
      (provider) => !this.priorities.includes(provider.user_id),
    );
  }

  passwordsMismatch(): boolean {
    if (!this.requiresPassword()) return false;
    const password = this.createUserForm.controls.password.value || '';
    const confirm = this.createUserForm.controls.confirm_password.value || '';
    return password !== confirm;
  }

  hasWhitespace(
    controlName: 'username' | 'password' | 'confirm_password',
  ): boolean {
    const control = this.createUserForm.controls[controlName];
    return (
      !!control.errors?.['whitespace'] && (control.touched || control.dirty)
    );
  }

  blockWhitespaceInput(event: KeyboardEvent): void {
    if (/\s/.test(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  sanitizeWhitespacePaste(
    event: ClipboardEvent,
    controlName: 'username' | 'password' | 'confirm_password',
  ): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!/\s/.test(pasted)) return;

    event.preventDefault();
    event.stopPropagation();

    const sanitized = pasted.replace(/\s+/g, '');
    const control = this.createUserForm.controls[controlName];
    const target = event.target as HTMLInputElement | null;
    const current = control.value || '';

    if (!target) {
      control.setValue(`${current}${sanitized}`);
      control.markAsDirty();
      return;
    }

    const start = target.selectionStart ?? current.length;
    const end = target.selectionEnd ?? start;
    const nextValue = `${current.slice(0, start)}${sanitized}${current.slice(end)}`;
    control.setValue(nextValue);
    control.markAsDirty();

    requestAnimationFrame(() => {
      const nextPosition = start + sanitized.length;
      target.setSelectionRange(nextPosition, nextPosition);
    });
  }

  isCustomConfiguration(): boolean {
    return this.createUserForm.controls.configuration_type.value === 'custom';
  }

  get configurationDescription(): string {
    return this.isCustomConfiguration()
      ? 'Se creará una configuración nueva antes de registrar el usuario.'
      : 'Usa la configuración predeterminada del sistema.';
  }

  get primaryActionLabel(): string {
    if (this.creatingStage === 'configuration') {
      return 'Creando configuración...';
    }

    if (this.creatingStage === 'user') {
      return 'Registrando usuario...';
    }

    const entity = this.isSubuserCreation() ? 'subusuario' : 'usuario';
    return this.activeStep === 1 ? 'Continuar' : `Registrar ${entity}`;
  }

  basicFieldsInvalid(): boolean {
    return (
      this.createUserForm.controls.username.invalid ||
      (this.requiresPassword() &&
        (this.createUserForm.controls.password.invalid ||
          this.createUserForm.controls.confirm_password.invalid)) ||
      (this.isSubuserCreation() &&
        this.isAdmin() &&
        this.createUserForm.controls.client_partner_id.invalid) ||
      this.requiredProfileFieldsInvalid() ||
      this.passwordsMismatch() ||
      this.hasIdentityInUse()
    );
  }

  hasIdentityInUse(): boolean {
    return (
      this.hasInUse('email') ||
      this.hasInUse(this.documentFieldName())
    );
  }

  hasInUse(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
  ): boolean {
    return !!this.identityInUse[fieldName];
  }

  documentFieldName(): 'document_number' | 'company_registration_number' {
    return this.isBusinessEntity()
      ? 'company_registration_number'
      : 'document_number';
  }

  private setInUse(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
    inUse: boolean,
  ): void {
    this.identityInUse[fieldName] = inUse;
    const control = this.createUserForm.controls[fieldName];
    const errors = { ...(control.errors || {}) };
    if (inUse) {
      errors['inUse'] = true;
    } else {
      delete errors['inUse'];
    }
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private clearIdentityInUse(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
  ): void {
    this.setInUse(fieldName, false);
  }

  private restoreIdentityInUseErrors(): void {
    (['email', 'document_number', 'company_registration_number'] as const).forEach(
      (fieldName) => {
        if (this.identityInUse[fieldName]) {
          this.setInUse(fieldName, true);
        }
      },
    );
  }

  onIdentityFieldBlur(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
  ): void {
    void this.checkIdentityField(fieldName);
  }

  private identityValue(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
  ): string {
    return String(this.createUserForm.controls[fieldName].value || '').trim();
  }

  private async checkIdentityField(
    fieldName: 'email' | 'document_number' | 'company_registration_number',
  ): Promise<boolean> {
    const value = this.identityValue(fieldName);
    if (!value) {
      this.setInUse(fieldName, false);
      return true;
    }
    if (fieldName === 'email' && this.createUserForm.controls.email.errors?.['email']) {
      this.setInUse(fieldName, false);
      return true;
    }

    this.uniquenessRequests += 1;
    this.checkingUniqueness = true;
    try {
      const resp: any = await firstValueFrom(
        this.userService.checkUserAvailability({ [fieldName]: value }),
      );
      const available = resp?.data?.[fieldName]?.available !== false;
      this.setInUse(fieldName, !available);
      return available;
    } catch {
      this.setInUse(fieldName, false);
      return true;
    } finally {
      this.uniquenessRequests = Math.max(0, this.uniquenessRequests - 1);
      this.checkingUniqueness = this.uniquenessRequests > 0;
    }
  }

  private async ensureIdentityAvailable(): Promise<boolean> {
    const checks: Array<Promise<boolean>> = [
      this.checkIdentityField('email'),
    ];
    const documentField = this.documentFieldName();
    if (this.identityValue(documentField)) {
      checks.push(this.checkIdentityField(documentField));
    }
    const results = await Promise.all(checks);
    return results.every(Boolean);
  }

  requiredProfileFieldsInvalid(): boolean {
    const requiredFields = [
      ...this.commonRequiredProfileFieldNames,
      ...(this.requiresAddress() ? this.addressRequiredProfileFieldNames : []),
      ...(this.isBusinessEntity()
        ? this.businessRequiredProfileFieldNames
        : this.naturalRequiredProfileFieldNames),
    ];
    return requiredFields.some(
      (fieldName) => this.createUserForm.controls[fieldName].invalid,
    );
  }

  goToAccessStep() {
    if (this.creating) return;
    this.activeStep = 1;
  }

  async goToConfigurationStep() {
    if (this.creating) return;

    if (this.basicFieldsInvalid()) {
      this.createUserForm.markAllAsTouched();
      return;
    }

    const available = await this.ensureIdentityAvailable();
    if (!available) {
      this.createUserForm.markAllAsTouched();
      return;
    }

    this.activeStep = 2;
  }

  handlePrimaryAction() {
    if (this.activeStep === 1) {
      void this.goToConfigurationStep();
      return;
    }

    void this.submit();
  }

  buildConfigurationPayload() {
    const schedule = this.scheduleDays.reduce(
      (acc, day) => {
        acc[day.name] = { from: day.from, to: day.to };
        return acc;
      },
      {} as Record<string, { from: string; to: string }>,
    );

    const form = this.createUserForm.getRawValue();

    return {
      joint_balance: !!form.joint_balance,
      single_currency: !!form.single_currency,
      fixed_fee: Number(form.fixed_fee ?? 0),
      percentage_fee: Number(form.percentage_fee ?? 0),
      max_p2p_amount:
        form.max_p2p_amount === null || form.max_p2p_amount === ('' as any)
          ? null
          : Number(form.max_p2p_amount),
      max_transfer_amount:
        form.max_transfer_amount === null ||
        form.max_transfer_amount === ('' as any)
          ? null
          : Number(form.max_transfer_amount),
      schedule,
      cashout_providers_priorities: [...this.priorities],
    };
  }

  buildUserPayload(configurationId: number | 'base') {
    const form = this.createUserForm.getRawValue();
    const userType = this.selectedUserType();
    const isSubuser = userType === 'subuser';
    const loggedId = Number(this.auth.user?.user_id);
    const parentId = isSubuser
      ? this.isAdmin()
        ? Number(form.client_partner_id)
        : loggedId
      : 1;

    const payload: any = {
      user_type: userType,
      entity_type: this.isBusinessEntity() ? 'business' : 'natural',
      username: (form.username || '').trim(),
      role_id: this.canChooseRole()
        ? Number(form.role_id) || Roles.Client
        : Roles.Client,
      configuration_id: configurationId,
      client_partner_id:
        parentId != null && Number.isFinite(parentId) && parentId > 0
          ? parentId
          : null,
      white_label: this.isAdmin() ? !!form.white_label : false,
      reliance_enabled: this.isAdmin() ? !!form.reliance_enabled : false,
      end_user_token_exp_days: Number(form.end_user_token_exp_days || 90),
    };

    if (this.isAdmin() && !isSubuser) {
      payload.status_compliance = form.status_compliance || 'pending';
    }

    if (!isSubuser) {
      payload.password = form.password || '';
    }

    this.commonRequiredProfileFieldNames.forEach((fieldName) => {
      payload[fieldName] = (form[fieldName] || '').trim();
    });
    this.addressRequiredProfileFieldNames.forEach((fieldName) => {
      payload[fieldName] = (form[fieldName] || '').trim();
    });
    payload.address_line_2 = (form.address_line_2 || '').trim();

    if (this.isBusinessEntity()) {
      this.businessProfileFieldNames.forEach((fieldName) => {
        payload[fieldName] = (form[fieldName] || '').trim();
      });
    } else {
      this.naturalProfileFieldNames.forEach((fieldName) => {
        payload[fieldName] = (form[fieldName] || '').trim();
      });
    }

    return payload;
  }

  resetForm() {
    this.createUserForm.reset({
      user_type: this.canCreateUsers() ? 'user' : 'subuser',
      entity_type: 'natural',
      username: '',
      password: '',
      confirm_password: '',
      role_id: null,
      status_compliance: 'pending',
      client_partner_id: this.defaultClientPartnerId(),
      white_label: false,
      reliance_enabled: false,
      end_user_token_exp_days: 90,
      email: '',
      first_name: '',
      middle_name: '',
      surname: '',
      second_surname: '',
      company_name: '',
      brand_name: '',
      company_registration_number: '',
      document_type: '',
      document_number: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      postal_code: '',
      state: '',
      country: '',
      configuration_type: 'base',
      joint_balance: false,
      single_currency: false,
      fixed_fee: 0,
      percentage_fee: 0,
      max_p2p_amount: null,
      max_transfer_amount: null,
      cashout_providers_priorities: [],
    });
    this.applyCreationTypeRules();
    this.activeStep = 1;
    this.creatingStage = null;
    this.customConfigurationInitialized = false;
    this.priorities = [];
    this.scheduleDays = DEFAULT_SCHEDULE_DAYS.map((name) => ({
      name,
      from: '00:00',
      to: '23:59',
    }));
    this.checkingUniqueness = false;
    this.uniquenessRequests = 0;
    this.identityInUse = {
      email: false,
      document_number: false,
      company_registration_number: false,
    };
    this.refreshAvailableProviders();
  }

  async submit() {
    if (
      this.creating ||
      this.createUserForm.invalid ||
      this.passwordsMismatch() ||
      (this.isSubuserCreation()
        ? !this.canCreateSubusers()
        : !this.canCreateUsers())
    ) {
      this.createUserForm.markAllAsTouched();
      return;
    }

    const available = await this.ensureIdentityAvailable();
    if (!available) {
      this.activeStep = 1;
      this.createUserForm.markAllAsTouched();
      return;
    }

    this.creating = true;
    this.creatingStage = this.isCustomConfiguration()
      ? 'configuration'
      : 'user';
    const createUserRequest = this.isCustomConfiguration()
      ? this.userService
          .createConfiguration(this.buildConfigurationPayload())
          .pipe(
            switchMap((configurationResp: any) => {
              const configurationId =
                configurationResp?.data?.configuration_id ||
                configurationResp?.configuration_id;

              if (!configurationId) {
                throw new Error(
                  'No se obtuvo el ID de la configuración creada.',
                );
              }

              this.creatingStage = 'user';
              return this.userService.createUser(
                this.buildUserPayload(configurationId),
              );
            }),
          )
      : this.userService.createUser(this.buildUserPayload('base'));

    createUserRequest.subscribe({
      next: (resp: any) => {
        this.creating = false;
        this.creatingStage = null;
        const created = resp?.data || null;
        this.userCreated.emit(created);
        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: resp?.message || 'Usuario registrado correctamente.',
        });
        this.setVisible(false);
      },
      error: (err) => {
        this.creating = false;
        this.creatingStage = null;
        const code = err?.error?.code || err?.code;
        if (code === 'DOCUMENT_IN_USE') {
          this.setInUse(this.documentFieldName(), true);
          this.activeStep = 1;
        } else if (code === 'EMAIL_IN_USE') {
          this.setInUse('email', true);
          this.activeStep = 1;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            err?.error?.message ||
            err?.message ||
            'Ocurrió un error al registrar el usuario.',
        });
      },
    });
  }

  addProviderToPriority() {
    this.addProviderModal = true;
    this.selectedProvider = null;
    this.refreshAvailableProviders();
  }

  addProviderToPriorityConfirmed() {
    if (!this.selectedProvider) return;
    this.priorities = [...this.priorities, this.selectedProvider.user_id];
    this.addProviderModal = false;
    this.refreshAvailableProviders();
  }

  moveUp(index: number) {
    if (index <= 0) return;
    [this.priorities[index], this.priorities[index - 1]] = [
      this.priorities[index - 1],
      this.priorities[index],
    ];
  }

  moveDown(index: number) {
    if (index >= this.priorities.length - 1) return;
    [this.priorities[index], this.priorities[index + 1]] = [
      this.priorities[index + 1],
      this.priorities[index],
    ];
  }

  removePriority(index: number) {
    this.priorities.splice(index, 1);
    this.refreshAvailableProviders();
  }

  trackByValue(_index: number, item: number) {
    return item;
  }
}
